import i18n from "../i18n";

export type Validator<T> = (value: T) => string | undefined;

// export const required: Validator<any> = (v) =>
//   v === undefined || v === null || v === ""
//     ? "This field is required"
//     : undefined;

export const required: Validator<any> = (v) => {
  const value = typeof v === "string" ? v.trim() : v;

  return value === undefined || value === null || value === ""
    ? i18n.t("validationErrors.required", { ns: "common" })
    : undefined;
};

export const positiveNumber: Validator<number> = (v) =>
  v <= 0 ? i18n.t("validationErrors.positiveNumber", { ns: "common" }) : undefined;

export const minLength =
  (len: number): Validator<string> =>
  (v) =>
    v.length < len ? i18n.t("validationErrors.minLength", { ns: "common", len }) : undefined;

// Email Validation: Standard regex for email format
export const email: Validator<string> = (v) => {
  if (!v) return undefined;
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return !regex.test(v) ? i18n.t("validationErrors.invalidEmail", { ns: "common" }) : undefined;
};

// Phone Validation (10-15 digits)
export const phone: Validator<string> = (v) => {
  if (!v) return undefined;
  // Count digits only: the placeholders show "+1 234 567 890", so a leading
  // "+", spaces, dashes and brackets are formatting, not part of the number.
  const digits = v.replace(/[\s()-]/g, "").replace(/^\+/, "");
  return !/^\d{10,15}$/.test(digits)
    ? i18n.t("validationErrors.invalidPhone", { ns: "common" })
    : undefined;
};

export const website = (val?: string) => {
  if (!val) return undefined;
  const urlPattern = /^(https?:\/\/)?([\w-]+\.)+[\w-]{2,}(\/.*)?$/i;
  return urlPattern.test(val.trim())
    ? undefined
    : i18n.t("validationErrors.invalidWebsite", { ns: "common" });
};

/* ---------------- Indian identifier formats ----------------
 *
 * All optional: an empty value passes, so these only ever complain about
 * something the user actually typed. The formats are fixed and well known, and
 * a mistyped one is worth catching here rather than at signing time.
 */

// Permanent Account Number: five letters, four digits, one letter.
export const pan: Validator<string> = (v) => {
  if (!v) return undefined;
  return /^[A-Z]{5}[0-9]{4}[A-Z]$/i.test(v.trim())
    ? undefined
    : i18n.t("validationErrors.invalidPan", { ns: "common" });
};

// GSTIN: 2-digit state code, the holder's PAN, entity number, "Z", checksum.
export const gstin: Validator<string> = (v) => {
  if (!v) return undefined;
  return /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z][0-9A-Z]Z[0-9A-Z]$/i.test(v.trim())
    ? undefined
    : i18n.t("validationErrors.invalidGst", { ns: "common" });
};

// Aadhaar: 12 digits, never starting 0 or 1. Spaces are allowed in, since it
// is commonly written in groups of four.
export const aadhaar: Validator<string> = (v) => {
  if (!v) return undefined;
  return /^[2-9][0-9]{11}$/.test(v.replace(/[\s-]/g, ""))
    ? undefined
    : i18n.t("validationErrors.invalidAadhaar", { ns: "common" });
};

// Indian PIN code: six digits, never starting 0.
export const pincode: Validator<string> = (v) => {
  if (!v) return undefined;
  return /^[1-9][0-9]{5}$/.test(v.trim())
    ? undefined
    : i18n.t("validationErrors.invalidPincode", { ns: "common" });
};

export const minValue =
  (min: number): Validator<number> =>
  (v) =>
    Number(v) < min ? i18n.t("validationErrors.minValue", { ns: "common", min }) : undefined;

export const maxValue =
  (max: number): Validator<number> =>
  (v) =>
    Number(v) > max ? i18n.t("validationErrors.maxValue", { ns: "common", max }) : undefined;

/* ---------------- Email list helpers ---------------- */

// Recipient inputs hold a list, either as a raw comma-separated string (the
// external review modal) or as an already-split array (the compose modal).
export const parseEmailList = (input: string | string[] | undefined) => {
  if (Array.isArray(input)) {
    return input.map((e) => e.trim()).filter((e) => e.length > 0);
  }
  return (input ?? "")
    .split(/[,;\s]+/)
    .map((e) => e.trim())
    .filter((e) => e.length > 0);
};

// `required` treats an empty array as present (it only checks for
// undefined/null/""), so a list-valued recipient field needs its own
// non-empty check or a send with no recipients passes validation.
export const requiredEmailList: Validator<string | string[]> = (v) => {
  const list = parseEmailList(v);
  if (list.length === 0) return i18n.t("validationErrors.required", { ns: "common" });
  return emailListFormat(v);
};

// Format-checks every address in the list. Empty input passes, so it can be
// used on optional fields such as CC.
export const emailListFormat: Validator<string | string[]> = (v) => {
  const invalid = parseEmailList(v).filter((e) => email(e));
  if (invalid.length === 0) return undefined;
  return invalid.length === 1
    ? i18n.t("validationErrors.invalidEmailOne", { ns: "common", email: invalid[0] })
    : i18n.t("validationErrors.invalidEmailMany", { ns: "common", emails: invalid.join(", ") });
};

/** Mirrors the Identity API's PasswordPolicy so the rule that fails shows
 *  under the field before the request is sent. Messages match the API's. */
export const strongPassword: Validator<string> = (v) => {
  const value = v ?? "";
  if (!value) return undefined;
  if (value.length < 8) return i18n.t("validationErrors.passwordLength", { ns: "common" });
  if (!/[A-Z]/.test(value)) return i18n.t("validationErrors.passwordUpper", { ns: "common" });
  if (!/[a-z]/.test(value)) return i18n.t("validationErrors.passwordLower", { ns: "common" });
  if (!/[0-9]/.test(value)) return i18n.t("validationErrors.passwordNumber", { ns: "common" });
  if (!/[^a-zA-Z0-9]/.test(value)) return i18n.t("validationErrors.passwordSpecial", { ns: "common" });
  return undefined;
};
